import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useProperty,
  usePropertyField,
  usePropertyName,
  usePropertyAddressUrl,
  usePrimaryColor,
  useSocialLinks,
  useCollectionBinding,
  usePage,
  useTokens,
  useActionUrls,
  useSlotGroupOverrides,
} from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import {
  getAccessibleTextOnBackground,
  getContrastTextColor,
  contrastRatio,
  TEXT_ON_PRIMARY_LIGHT,
  TEXT_ON_PRIMARY_DARK,
} from '../utils/luminance';
import {
  FacebookIcon,
  InstagramIcon,
  TwitterIcon,
  YouTubeIcon,
  LinkedInIcon,
  TikTokIcon,
} from '@vibesites/assets';
import { buildNavLinks, hasSiteMapImageOverride, normalizePath, type NavLink } from './_navShared';
import { useFloorPlansRedirect } from './_floorPlansRedirect';

// ============================================================================
// Dynamic Navigation from pages.json
// ----------------------------------------------------------------------------
// The nav eligibility filter and link-tree builder live in ./_navShared so the
// sitemap page can mirror the exact same "pages in navigation" set. See
// buildNavLinks / getEligibleNavPages there.
// ============================================================================

interface VirtualTour {
  virtual_tour_url?: string | null;
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_primary_logo', suggestedFields: ['property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

// Logo shown at the top of the tablet / mobile drawer, replacing the plain
// property-name wordmark. The drawer sits on `--vibe-color-primary`, so the
// runtime picks among white / dark / primary logo fields for contrast (see
// `runtimeDrawerLogoSlotDef` in the component) and whitening when needed —
// mirroring the footer logo convention in Footer.tsx.
const drawerLogoSlotDef: SlotGroupDefinition = {
  label: 'Drawer Logo',
  fields: {
    image: {
      label: 'Logo Image',
      type: 'image',
      defaultField: 'property_primary_logo',
      suggestedFields: [
        'property_primary_logo',
        'property_mono_white_logo',
        'property_mono_black_logo',
      ],
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

// Primary header CTA — "Schedule a Tour" button to the right of the nav
// links. Contact Us, Apply Now, and Resident Login are plain nav links
// sourced from pages.json.
const contactLinkDef: SlotGroupDefinition = {
  label: 'Header CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/contact-us' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// Address row inside the mobile drawer. Promoted from a bare
// `header.full_address` content token + auto-generated Google Maps
// directions URL to a slot group so editors can pick the address text,
// the destination URL, and the link target independently — rather than
// having the URL forced to the property's `google_place_url` (or a
// synthesized `/maps/dir/?destination=...` fallback) with no override.
const addressLinkDef: SlotGroupDefinition = {
  label: 'Address Link',
  fields: {
    address: {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street, Atlanta, GA 30313',
    },
    // URL has no defaultField — defaults to usePropertyAddressUrl() at render
    // time (mobile-safe Google Maps URL built from place / business / full
    // address / lat-lon fallbacks). An explicit editor override wins.
    url: {
      label: 'URL',
      type: 'url',
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ============================================================================
// Site-wide CTA styling
// ============================================================================

// Every CTA on the site (including inherited parent-theme sections) uses the
// --bh-cta fill (property "Color 5") with white text. Rendered from Header
// because it is on every page and index.css isn't loaded in the editor.
// !important is required: the lead-form button sets its colors inline.
const CTA_SELECTORS = [
  '.hero-cta',
  '.nav-cta-btn',
  '.mobile-menu-panel a.nav-drawer-cta',
  '.po-cta',
  '.da-cta',
  '.pc-cta',
  '.af-cta',
  '.cf-cta-btn',
  '.tabbed-cta',
  '.fpi-cta',
  '.ai-cta',
  '.gallery-photos__cta',
  '.fqi-cta',
  '.imi-cta',
  '.vti-cta',
  '.pet-cta',
  '.pec-cta--solid',
  '.gtc-cta--outline',
  '.ano-btn:not(.ano-btn--disabled)',
  '.ra-btn:not(.ra-btn--disabled)',
  '.mh-lead-form-submit-button:not(:disabled)',
  // Tour-scheduler block (Tailwind-only markup): empty-state "Contact Us"
  // link and the Continue / Schedule Tour nav button.
  '.stf-form-wrap a.bg-primary',
  '.stf-form-wrap button.bg-primary.ml-auto',
];

const CTA_FILL = 'var(--bh-cta, var(--vibe-color-primary))';
const CTA_FILL_HOVER = `color-mix(in srgb, ${CTA_FILL}, black 12%)`;

const CTA_OVERRIDE_CSS = `
${CTA_SELECTORS.map((s) => `:root ${s}`).join(',\n')} {
  background: ${CTA_FILL} !important;
  border: 1.5px solid ${CTA_FILL} !important;
  color: #fff !important;
}
${CTA_SELECTORS.map((s) => `:root ${s}:hover`).join(',\n')} {
  background: ${CTA_FILL_HOVER} !important;
  border-color: ${CTA_FILL_HOVER} !important;
  color: #fff !important;
  box-shadow: 0 6px 18px color-mix(in srgb, ${CTA_FILL} 30%, transparent) !important;
}
${CTA_SELECTORS.map((s) => `:root ${s} *`).join(',\n')} {
  color: inherit !important;
}
:root .tabbed-cta::before,
:root .gtc-cta--outline::before {
  display: none !important;
}
`;

// ============================================================================
// SVG Icon Components
// ============================================================================


// Dot-separated format (516.916.6886) to match the drawer / footer treatment.
function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
}

function hasValue(val: string | null | undefined): val is string {
  return !!val && val.trim() !== '';
}

/**
 * Splits a single-line address into a street line and a "city, state zip" line
 * so the drawer address renders on two lines (matching the reference). Drops a
 * trailing country segment. Falls back to a single line when there are no commas.
 */
function splitAddressLines(full: string | null | undefined): { line1: string; line2: string } {
  const cleaned = (full || '')
    .trim()
    .replace(/,?\s*(usa|u\.s\.a\.|united states(?: of america)?)\.?\s*$/i, '')
    .trim();
  const parts = cleaned.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length <= 1) return { line1: cleaned, line2: '' };
  return { line1: parts[0], line2: parts.slice(1).join(', ') };
}

// ============================================================================
// Chevron Down Icon for "More" dropdown
// ============================================================================

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

// Close (X) icon for the mobile / tablet drawer's circular close button.
function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

// ============================================================================
// Desktop Nav Link
// ============================================================================

function DesktopNavLink({ link, currentPath }: { link: NavLink; currentPath: string }) {
  const hasChildren = !!(link.children && link.children.length);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // A link is "active" (persistent double-bar) when the visitor is on its
  // page — or, for a parent with a dropdown, on any of its child pages — so
  // the top + bottom lines that normally appear on hover stay lit to show
  // which interior/banner page is currently in view. `currentPath` comes from
  // the runtime page context (see DesktopNav), so it's correct at render time.
  const isActive =
    !!currentPath &&
    (normalizePath(link.href) === currentPath ||
      (link.children?.some((child) => normalizePath(child.href) === currentPath) ?? false));

  // Click-outside / Escape close — mirrors the "More" dropdown so the menu
  // dismisses cleanly when focus or pointer leaves the parent group.
  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  if (!hasChildren) {
    return (
      <a
        href={link.href}
        target={link.target || undefined}
        rel={link.target === '_blank' ? 'noopener noreferrer' : undefined}
        data-vibesite-event={link.event}
        aria-current={!link.target && isActive ? 'page' : undefined}
        className={`nav-tb-hover font-body text-[15px] font-normal tracking-normal text-[var(--bh-header-fg)] transition-colors py-1.5 whitespace-nowrap${
          !link.target && isActive ? ' nav-tb-active' : ''
        }`}
      >
        {link.label}
      </a>
    );
  }

  return (
    <div
      ref={wrapperRef}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      {/* The parent label is still a real link so clicking it navigates to
          the parent page; the chevron + dropdown surface the child pages on
          hover/focus without stealing the primary affordance. */}
      <a
        href={link.href}
        aria-current={isActive ? 'page' : undefined}
        className={`nav-tb-hover font-body text-[15px] font-normal tracking-normal text-[var(--bh-header-fg)] transition-colors py-1.5 flex items-center gap-1 whitespace-nowrap${
          isActive ? ' nav-tb-active' : ''
        }`}
        aria-haspopup="true"
        aria-expanded={open}
        onFocus={() => setOpen(true)}
      >
        {link.label}
        <ChevronDownIcon
          className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </a>

      <div
        className={`nav-more-dropdown absolute left-0 top-full mt-1 border border-[var(--vibe-color-border)] shadow-lg min-w-[200px] z-[70] transition-all duration-200 ${
          open
            ? 'opacity-100 visible translate-y-0'
            : 'opacity-0 invisible -translate-y-1'
        }`}
        style={{ backgroundColor: 'var(--vibe-color-background)' }}
      >
        {link.children!.map((child) => (
          <a
            key={child.href}
            href={child.href}
            target={child.target || undefined}
            rel={child.target === '_blank' ? 'noopener noreferrer' : undefined}
            aria-current={
              !child.target && normalizePath(child.href) === currentPath ? 'page' : undefined
            }
            // Hit area stays full-row; the animated double-bar (same as
            // top-level desktop nav) lives on the label so it hugs the text.
            className="block px-5 py-3 font-body text-[15px] font-normal tracking-normal text-[var(--vibe-color-text)] transition-colors"
          >
            <span className="nav-tb-hover inline-block py-1.5">{child.label}</span>
          </a>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Desktop Navigation with dynamic "More" overflow dropdown
// ============================================================================

function DesktopNav({ navLinks }: { navLinks: NavLink[] }) {
  const navContainerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(navLinks.length);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);

  // Current page path from the runtime context — the authoritative source for
  // "which page am I on". Using `usePage()` (rather than
  // `window.location.pathname`) means the active nav link (persistent
  // double-bar) is correct at SSR/first paint AND inside the editor/preview
  // iframe, whose browser URL is NOT the site's clean page path.
  const currentPath = normalizePath(usePage()?.path || '');

  const recalculate = useCallback(() => {
    const container = navContainerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const availableWidth = container.offsetWidth;
    const children = Array.from(measure.children) as HTMLElement[];
    const computedGap = parseFloat(getComputedStyle(container).gap) || 20;
    const moreButtonWidth = 90;

    let totalWidth = 0;
    let fitCount = 0;

    for (let i = 0; i < children.length; i++) {
      const childWidth = children[i].offsetWidth;
      const widthWithGap = i === 0 ? childWidth : childWidth + computedGap;
      const needed = totalWidth + widthWithGap + (i < children.length - 1 ? moreButtonWidth + computedGap : 0);

      if (needed > availableWidth && i < children.length) {
        const remainingWidth = totalWidth + widthWithGap;
        let allFit = true;
        let testWidth = remainingWidth;
        for (let j = i + 1; j < children.length; j++) {
          testWidth += children[j].offsetWidth + computedGap;
          if (testWidth > availableWidth) {
            allFit = false;
            break;
          }
        }
        if (allFit) {
          fitCount = children.length;
        } else {
          fitCount = i;
        }
        break;
      }
      totalWidth += widthWithGap;
      fitCount = i + 1;
    }

    setVisibleCount(fitCount);
  }, [navLinks.length]);

  useEffect(() => {
    recalculate();
    const observer = new ResizeObserver(() => recalculate());
    if (navContainerRef.current) {
      observer.observe(navContainerRef.current);
    }
    return () => observer.disconnect();
  }, [recalculate]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
    }
    if (moreOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [moreOpen]);

  const visibleLinks = navLinks.slice(0, visibleCount);
  const overflowLinks = navLinks.slice(visibleCount);

  // Light the "More" button's double-bar when the current page has been
  // pushed into the overflow dropdown, so the active-page indicator survives
  // at narrow widths instead of silently disappearing with the link.
  const moreActive =
    !!currentPath &&
    overflowLinks.some(
      (link) =>
        (!link.target && normalizePath(link.href) === currentPath) ||
        (link.children?.some(
          (child) => !child.target && normalizePath(child.href) === currentPath,
        ) ?? false),
    );

  return (
    <>
      {/* Hidden measurement row. Parent links with children render with a
          chevron in the live nav, so we mirror that here so the per-link
          offsetWidth used by the overflow calculation matches the visible
          width — otherwise the chevron would push the rightmost link out
          unexpectedly when the row is near full. */}
      <div
        ref={measureRef}
        aria-hidden="true"
        className="flex items-center gap-5 absolute pointer-events-none"
        style={{ visibility: 'hidden', position: 'absolute', top: 0, left: 0, whiteSpace: 'nowrap' }}
      >
        {navLinks.map((link) => (
          <span
            key={link.href}
            className="font-body text-[15px] font-normal tracking-normal inline-flex items-center gap-1"
          >
            {link.label}
            {link.children && link.children.length ? (
              <ChevronDownIcon className="w-3.5 h-3.5" />
            ) : null}
          </span>
        ))}
      </div>

      {/* Visible navigation */}
      <nav
        ref={navContainerRef}
        className="hidden lg:flex items-center justify-center gap-5 xl:gap-7 flex-1 min-w-0 mx-6"
        role="navigation"
        aria-label="Main navigation"
      >
        {visibleLinks.map((link) => (
          <DesktopNavLink key={link.href} link={link} currentPath={currentPath} />
        ))}

        {overflowLinks.length > 0 && (
          <div
            ref={moreRef}
            className="relative"
            // Mirror the DesktopNavLink hover behavior — the "More"
            // overflow dropdown opens on hover/focus just like the
            // "Floor Plans" parent dropdown so the two affordances feel
            // identical. The click handler stays as a fallback for
            // touch devices where hover doesn't fire, and the
            // click-outside listener still dismisses the menu when the
            // pointer leaves the group entirely.
            onMouseEnter={() => setMoreOpen(true)}
            onMouseLeave={() => setMoreOpen(false)}
          >
            <button
              onClick={() => setMoreOpen(!moreOpen)}
              onFocus={() => setMoreOpen(true)}
              className={`nav-tb-hover font-body text-[15px] font-normal tracking-normal text-[var(--bh-header-fg)] transition-colors py-1.5 flex items-center gap-1 whitespace-nowrap${
                moreActive ? ' nav-tb-active' : ''
              }`}
              aria-expanded={moreOpen}
              aria-haspopup="true"
            >
              More
              <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform duration-200 ${moreOpen ? 'rotate-180' : ''}`} />
            </button>

            <div
              className={`nav-more-dropdown absolute right-0 top-full mt-1 border border-[var(--vibe-color-border)] shadow-lg min-w-[200px] z-[70] transition-all duration-200 ${
                moreOpen
                  ? 'opacity-100 visible translate-y-0'
                  : 'opacity-0 invisible -translate-y-1'
              }`}
              style={{ backgroundColor: 'var(--vibe-color-background)' }}
            >
              {overflowLinks.map((link) => (
                <React.Fragment key={link.href}>
                  <a
                    href={link.href}
                    target={link.target || undefined}
                    rel={link.target === '_blank' ? 'noopener noreferrer' : undefined}
                    data-vibesite-event={link.event}
                    aria-current={
                      !link.target && normalizePath(link.href) === currentPath ? 'page' : undefined
                    }
                    // Hit area stays full-row; double-bar matches top-level nav.
                    className="block px-5 py-3 font-body text-[15px] font-normal tracking-normal text-[var(--vibe-color-text)] transition-colors"
                  >
                    <span className="nav-tb-hover inline-block py-1.5">{link.label}</span>
                  </a>
                  {link.children?.map((child) => (
                    <a
                      key={child.href}
                      href={child.href}
                      target={child.target || undefined}
                      rel={child.target === '_blank' ? 'noopener noreferrer' : undefined}
                      data-vibesite-event={child.event}
                      aria-current={
                        !child.target && normalizePath(child.href) === currentPath
                          ? 'page'
                          : undefined
                      }
                      // Indent child rows so the More dropdown reads as a
                      // nested list when a parent (e.g. Floor Plans) gets
                      // pushed into overflow and we want its sub-items to
                      // remain reachable from the dropdown. Same hover as
                      // top-level desktop nav (e.g. Property Map nest).
                      className="block pl-9 pr-5 py-3 font-body text-[15px] font-normal tracking-normal text-[var(--vibe-color-text-muted)] transition-colors"
                    >
                      <span className="nav-tb-hover inline-block py-1.5">{child.label}</span>
                    </a>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </nav>
    </>
  );
}

// ============================================================================
// Mobile Nav Link
// ============================================================================

function MobileNavLink({
  link,
  onNavigate,
  isChild,
}: {
  link: NavLink;
  onNavigate: () => void;
  /** Child (sub-nav) rows render one step smaller and muted so a parent's
      sub-items read as nested without an indent that looks misaligned in a
      vertical list. */
  isChild?: boolean;
}) {
  return (
    <a
      href={link.href}
      target={link.target || undefined}
      rel={link.target === '_blank' ? 'noopener noreferrer' : undefined}
      data-vibesite-event={link.event}
      onClick={onNavigate}
      className={`rise-nav-darken-hover block py-2.5 font-body font-normal leading-tight transition-colors ${
        isChild
          ? 'text-[16px] text-[var(--vibe-color-text-on-primary)]/70'
          : 'text-[20px] text-[var(--vibe-color-text-on-primary)]'
      }`}
    >
      {link.label}
    </a>
  );
}

// ============================================================================
// Main Header Component
// ============================================================================
// STRUCTURE: Single navigation bar (logo | nav links | utility + CTA).
// The old 3-bar stack (banner + utility bar + nav bar) is replaced by one
// unified bar. Utility links (phone, Apply Now, Resident Access) are
// inlined into the right side of the nav bar on desktop, and folded into
// the mobile drawer on small screens.
// ============================================================================

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  useFloorPlansRedirect();

  // Virtual tours — controls nav-link visibility
  const virtualTours = useCollectionBinding<VirtualTour>('header.virtual_tours', { collection: 'virtual_tours' });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  // Interactive Map — drops out of the nav when the property has no
  // ApartmentSync (Repli360 / RRAC) IDs to drive the "interactive
  // leasing view" widget on that page. We require BOTH the JavaScript
  // ID (script URL token) and the Config ID (site identifier) — a
  // property with only one populated is a half-configured state and
  // shouldn't show the page (e.g. Centre Point, which has neither, was
  // leaking into the nav when only the JavaScript ID was checked).
  // Mirrors the SSG-side hide + noindex logic in ssg-entry.ts so
  // navigation, sitemap, and crawler hints stay consistent. The fields
  // are custom property fields (typed as `text`) so we read them via
  // `useProperty()` directly rather than the strongly-typed
  // `usePropertyField` (which only knows the built-in scalar fields).
  const propertyForLeasing = useProperty() as Record<string, unknown>;
  const apartmentSyncJsId = propertyForLeasing?.custom_apartmentsync_javascript_id as
    | string
    | null
    | undefined;
  const apartmentSyncConfigId = propertyForLeasing?.custom_apartmentsync_config_id as
    | string
    | null
    | undefined;

  // Editor-configurable override. The `header.interactive_map_show_if_exists`
  // token can be bound to a property field (Field mode — e.g.
  // `custom_engrain_sightmap_url`) or given a literal string (Custom mode).
  // In both cases useContentToken returns the already-RESOLVED value (the
  // field's value, or the literal), so we simply expose the Interactive Map
  // page whenever that resolved value is non-empty. Blank by default (no
  // override). ADDITIVE to the ApartmentSync detection above, so existing
  // ApartmentSync-configured properties keep working untouched.
  const interactiveMapShowIfExists = useContentToken('header.interactive_map_show_if_exists');
  const hasInteractiveMapOverride = !!(
    interactiveMapShowIfExists && String(interactiveMapShowIfExists).trim()
  );

  const hasInteractiveLeasing =
    (!!(apartmentSyncJsId && String(apartmentSyncJsId).trim()) &&
      !!(apartmentSyncConfigId && String(apartmentSyncConfigId).trim())) ||
    hasInteractiveMapOverride;

  // Property Map — appears under Floor Plans only when SiteMapImage › image
  // has an uploaded value (overrides live on the property-map page, so scan
  // the full override map rather than the current page's scoped resolve).
  const hasSiteMapImage = hasSiteMapImageOverride(useSlotGroupOverrides());

  // OLL (online leasing) links out instead of the in-theme /apply-now page
  // (hidden). Resident Login is omitted until the property has a portal.
  const actionUrls = useActionUrls();
  const applyNowUrl = actionUrls.onlineLeasing || actionUrls.applyNow || '';

  const navLinks = useMemo(
    () =>
      buildNavLinks(hasVirtualTours, hasInteractiveLeasing, hasSiteMapImage, {
        applyNowUrl,
      }),
    [
      hasVirtualTours,
      hasInteractiveLeasing,
      hasSiteMapImage,
      applyNowUrl,
    ],
  );

  // Content tokens
  const phone = useContentToken('header.phone');

  // ---- Surface / background contrast decisions ----
  // The header bar is painted with --vibe-color-surface, and its dropdown
  // panels with --vibe-color-background. Unlike primary/secondary, NEITHER of
  // those tokens has an auto-computed contrast color (the tokens generator only
  // derives text-on-primary / text-on-secondary), and both are freely
  // property-overridable. Some properties set a DARK surface/background (e.g.
  // American Landmark's Evolv), which turns the previously hardcoded dark logo +
  // --vibe-color-text nav illegible. So mirror the same contrast decision the
  // drawer already makes for the primary color: when the surface (or the
  // dropdown's background) is dark, flip the foreground to white and whiten the
  // logo; when it's light, keep the theme's own dark text + mono-black logo so
  // existing light-surface properties render exactly as before.
  const themeColors = useTokens().color;
  const darkTextOnSurface =
    getContrastTextColor(themeColors.surface) === TEXT_ON_PRIMARY_DARK;
  const darkTextOnBackground =
    getContrastTextColor(themeColors.background) === TEXT_ON_PRIMARY_DARK;
  // Foreground colors exposed to the nav CSS as local custom properties (set on
  // <header> below). On a light surface these resolve to the theme's own dark
  // text token — preserving current behavior; on a dark surface they flip white.
  const headerFg = darkTextOnSurface ? 'var(--vibe-color-text)' : TEXT_ON_PRIMARY_LIGHT;
  const dropdownFg = darkTextOnBackground ? 'var(--vibe-color-text)' : TEXT_ON_PRIMARY_LIGHT;

  // Slot groups
  const logoRaw = useSlotGroup('header.logo', logoSlotDef);
  const monoBlackLogo = usePropertyField('property_mono_black_logo');

  // Logo source: prefer the property's mono_black wordmark, then fall back to
  // whatever the editor picked (typically the full-color logo). On a dark
  // surface that mark would wash out, so we recolor it to white with the same
  // `brightness(0) invert(1)` filter the footer uses (see `whitenHeaderLogo`
  // below) rather than swapping to `property_mono_white_logo`, which for some
  // properties is only a brand glyph (a paw) instead of the full wordmark.
  const logo = { ...logoRaw, alt: logoRaw.alt, image: monoBlackLogo || logoRaw.image };
  const whitenHeaderLogo = !darkTextOnSurface;

  const contactLink = useSlotGroup('header.contact_link', contactLinkDef);
  const addressLink = useSlotGroup('header.address_link', addressLinkDef);
  // Fallback URL when the editor hasn't set a custom address link — hook
  // builds a mobile-safe Google Maps URL from property place / business /
  // full-address / lat-lon fields, in that order.
  const propertyAddressUrl = usePropertyAddressUrl();

  // Drawer brand mark — a property logo renders at the top of the drawer,
  // linking home. The drawer sits on `--vibe-color-primary`, so pick the logo
  // version that stays readable against that fill:
  //   • dark primary (needs light/white mark) → prefer the mono black or
  //     primary wordmark, then whiten with the same CSS filter the footer uses.
  //     Fall back to mono white only when no wordmark is configured — for some
  //     properties mono_white is only a brand glyph (a paw), so it is last.
  //   • light primary (needs dark mark) → mono black when available; else primary
  // Property name is kept as alt text + text fallback when no logo exists.
  const propertyName = usePropertyName();
  const primaryColor = usePrimaryColor();
  const accessibleTextOnPrimary = useMemo(
    () => getAccessibleTextOnBackground(primaryColor, themeColors.textOnPrimary),
    [primaryColor, themeColors.textOnPrimary],
  );

  // Drawer CTA sits on primary. Surface-on-primary
  // fails when both are mid-tones (this property: taupe `property_color_3`
  // vs gray primary, ~1.6:1). WCAG 3:1
  // is the UI-component floor; below that we flip to the drawer's contrast
  // color so those controls stay readable. The header-bar CTA and
  // hamburger stay on the original primary pairing.
  const MIN_UI_CONTRAST = 3;
  const surfaceColor = themeColors.surface;
  const drawerChromeUsesSurface =
    contrastRatio(surfaceColor, primaryColor) >= MIN_UI_CONTRAST;
  const drawerChromeBg = drawerChromeUsesSurface
    ? surfaceColor
    : getContrastTextColor(primaryColor);
  const drawerChromeFg = getAccessibleTextOnBackground(
    drawerChromeBg,
    drawerChromeUsesSurface ? primaryColor : undefined,
  );
  // Site-wide CTA fill and the hamburger disc use the property's "Color 5";
  // both fall back to primary when that field is empty. CTA labels are always
  // white by design. Consumed by CTA_OVERRIDE_CSS and the components' own CTA rules.
  const color5 = (usePropertyField('property_color_5') as string | null | undefined)?.trim();
  const hamburgerBg = color5 || primaryColor;
  const hamburgerFg = TEXT_ON_PRIMARY_LIGHT;
  const useWhiteOnPrimary = accessibleTextOnPrimary === TEXT_ON_PRIMARY_LIGHT;

  const ctaColor = color5 || primaryColor;
  const ctaTextColor = '#fff';
  const monoWhiteLogo = usePropertyField('property_mono_white_logo');
  const primaryLogo = usePropertyField('property_primary_logo');

  const drawerLogoDefaultField = useMemo(() => {
    if (useWhiteOnPrimary) {
      if (hasValue(monoBlackLogo)) return 'property_mono_black_logo';
      if (hasValue(primaryLogo)) return 'property_primary_logo';
      if (hasValue(monoWhiteLogo)) return 'property_mono_white_logo';
      return 'property_primary_logo';
    }
    if (hasValue(monoBlackLogo)) return 'property_mono_black_logo';
    if (hasValue(primaryLogo)) return 'property_primary_logo';
    return 'property_primary_logo';
  }, [useWhiteOnPrimary, monoBlackLogo, primaryLogo, monoWhiteLogo]);

  // Whiten dark/color sources on a dark primary so the mark reads as white.
  // Skip when we already resolved to the dedicated mono-white asset.
  const whitenDrawerLogo =
    useWhiteOnPrimary && drawerLogoDefaultField !== 'property_mono_white_logo';

  const runtimeDrawerLogoSlotDef: SlotGroupDefinition = useMemo(
    () => ({
      ...drawerLogoSlotDef,
      fields: {
        ...drawerLogoSlotDef.fields,
        image: {
          ...drawerLogoSlotDef.fields.image,
          defaultField: drawerLogoDefaultField,
        },
      },
    }),
    [drawerLogoDefaultField],
  );
  const drawerLogo = useSlotGroup('header.drawer_logo', runtimeDrawerLogoSlotDef);

  // Two-line address for the drawer footer block.
  const { line1: addressLine1, line2: addressLine2 } = splitAddressLines(addressLink.address);

  // Social links — sourced from property fields; only configured platforms
  // render, each as a circular outlined icon on the dark drawer background.
  const social = useSocialLinks();
  const socialLinks = [
    { url: social.facebook, Icon: FacebookIcon, label: 'Facebook' },
    { url: social.instagram, Icon: InstagramIcon, label: 'Instagram' },
    { url: social.twitter, Icon: TwitterIcon, label: 'Twitter' },
    { url: social.youtube, Icon: YouTubeIcon, label: 'YouTube' },
    { url: social.linkedin, Icon: LinkedInIcon, label: 'LinkedIn' },
    { url: social.tiktok, Icon: TikTokIcon, label: 'TikTok' },
  ].filter((s): s is { url: string; Icon: typeof FacebookIcon; label: string } => hasValue(s.url));

  // Font loading is handled by the platform, not this theme: each property's
  // CMS picks heading/body fonts from the shared registry (SUPPORTED_FONTS),
  // font-bake injects the selected font on published sites, and the
  // editor/preview index.html preloads every supported family. The
  // --vibe-font-heading / --vibe-font-body tokens (see tokens.ts) then decide
  // which of those loaded families each element renders in. So we deliberately
  // do NOT hardcode a Google Fonts <link> here — doing so would force one
  // family regardless of the property's token selection.

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const closeMobileMenu = () => setMenuOpen(false);

  return (
    <>
      {/* Property primary colors are often medium tones (gold, tan) where the
          tokens runtime picks white on-primary text for aesthetics, but that
          pairing can fail WCAG AA (e.g. #fff on #B28C59 ≈ 3.09:1). Recompute
          an accessible value once here so every consumer of
          --vibe-color-text-on-primary / text-primary-foreground site-wide
          (hero CTAs, footer, tabbed callouts, etc.) stays legible. */}
      <style>{`:root { --vibe-color-text-on-primary: ${accessibleTextOnPrimary}; --bh-cta: ${ctaColor}; --bh-cta-fg: ${ctaTextColor}; }`}</style>
      <style>{CTA_OVERRIDE_CSS}</style>

      {/* Solid header — an opaque --vibe-color-surface band fixed to the top of
          the viewport, matching the reference design. It overlays the top of
          the hero (the full-bleed image continues below the bar), and interior
          `HeroSolidBG` pages already pad their content down to clear it. The nav
          text + logo pick their contrast side from the surface color via the
          `--bh-header-fg` / `--bh-header-dropdown-fg` custom properties below,
          so they stay legible whether the surface is light (cream) or dark. */}
      <header
        className="header-solid fixed left-0 right-0 z-50 w-full"
        style={{
          top: 0,
          backgroundColor: 'var(--vibe-color-surface)',
          '--bh-header-fg': headerFg,
          '--bh-header-dropdown-fg': dropdownFg,
        } as React.CSSProperties}
      >
        {/* Editor-only control. Holds the name of the property field whose
            presence exposes the Interactive Map page in the nav (consumed by
            `hasInteractiveMapOverride` above — e.g. `custom_engrain_sightmap_url`).
            Rendered visually hidden because it's a configuration value, not
            display copy, but wrapped in EditableToken so it's reachable from
            the editor sidebar / inline editor and satisfies the schema linter. */}
        <EditableToken
          path="header.interactive_map_show_if_exists"
          type="text"
          label="Interactive Map: Show If Exists"
          description="Expose the Interactive Map page in the navigation if this token or field contains a value (e.g. custom_engrain_sightmap_url). Leave blank to hide."
          as="span"
          className="sr-only"
        >
          {interactiveMapShowIfExists || ''}
        </EditableToken>

        {/* ============================================================== */}
        {/* Single unified navigation bar                                  */}
        {/* ============================================================== */}
        <div
          className="header-bar"
          style={{ paddingTop: 'var(--offer-banner-height, 0px)', transition: 'padding-top 0.5s cubic-bezier(0.4, 0, 0.2, 1)' }}
        >
          {/* Single clean bar: logo · nav links · Contact Us CTA. The phone +
              address utility row was removed to match the reference design;
              the phone and address remain available in the mobile drawer and
              the footer. */}
          <div className="px-4 sm:px-6 lg:px-8">
            {/* `relative` so the logo can be absolutely centered in the
                collapsed (below-lg) layout: hamburger left · logo centered ·
                Contact Us CTA right. On lg+ the hamburger is hidden and the
                logo returns to the left of the centered nav links. */}
            <div className="relative flex items-center h-[84px] md:h-[100px] lg:h-[92px]">
              {/* Hamburger — collapsed (tablet / mobile) only, pinned left as
                  a filled circular button to match the reference layout. */}
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="lg:hidden relative w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-full"
                style={{ backgroundColor: hamburgerBg }}
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={menuOpen}
              >
                <span
                  className={`absolute block w-5 h-0.5 transition-all duration-300 ${
                    menuOpen ? 'rotate-45' : '-translate-y-[5px]'
                  }`}
                  style={{ backgroundColor: hamburgerFg }}
                />
                <span
                  className={`absolute block w-5 h-0.5 transition-all duration-300 ${
                    menuOpen ? 'opacity-0' : ''
                  }`}
                  style={{ backgroundColor: hamburgerFg }}
                />
                <span
                  className={`absolute block w-5 h-0.5 transition-all duration-300 ${
                    menuOpen ? '-rotate-45' : 'translate-y-[5px]'
                  }`}
                  style={{ backgroundColor: hamburgerFg }}
                />
              </button>

              {/* Logo positioning by breakpoint:
                  • Mobile (< md): CTA is hidden, so the logo is absolutely
                    centered on the whole bar (dead-center, just the hamburger
                    at left).
                  • Tablet (md–lg): both hamburger (left) and Contact Us CTA
                    (right) are visible, and the CTA is much wider than the
                    hamburger. Dead-centering would make the logo look shifted
                    toward the hamburger, so instead the wrapper takes the flex
                    space BETWEEN the two controls (flex-1) and centers the logo
                    within it, then nudges it ~50px to the right (translate-x)
                    so it reads as balanced against the wider CTA without having
                    to be mathematically centered in the gap.
                  • Desktop (lg+): static, sitting at the left before the nav. */}
              <EditableSlotGroup
                groupPath="header.logo"
                definition={logoSlotDef}
                className="md:flex md:flex-1 md:min-w-0 md:justify-center lg:flex-none"
              >
                <a
                  href="/"
                  aria-label="Go to homepage"
                  className="flex-shrink-0 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 md:static md:left-auto md:top-auto md:translate-x-[50px] md:translate-y-0 lg:translate-x-0"
                >
                  {logo.image ? (
                    <VibesitesImage
                      url={logo.image}
                      alt={logo.alt || 'Community Logo'}
                      className="h-14 lg:h-16 w-auto max-w-[240px] object-contain"
                      sizes="240px"
                      style={whitenHeaderLogo ? { filter: 'brightness(0) invert(1)' } : undefined}
                    />
                  ) : (
                    <div className="font-heading text-xl lg:text-2xl font-bold text-[var(--vibe-color-primary)]">
                      {propertyName || 'Blue Haven'}
                    </div>
                  )}
                </a>
              </EditableSlotGroup>

              {/* Desktop Navigation Links */}
              <DesktopNav navLinks={navLinks} />

              {/* Right group — the primary Schedule a Tour CTA. */}
              <div className="flex items-center gap-3 flex-shrink-0 ml-auto">
                <EditableSlotGroup groupPath="header.contact_link" definition={contactLinkDef}>
                  <a
                    href={contactLink.url || '/contact-us'}
                    target={contactLink._target || '_self'}
                    className="nav-cta-btn group/cta relative hidden md:inline-flex items-center gap-2 whitespace-nowrap px-6 py-3 font-heading text-sm md:px-4 md:tracking-[0.1em] lg:px-8 lg:py-3.5 lg:text-base lg:tracking-[0.15em] font-normal tracking-[0.15em] uppercase overflow-hidden transition-all duration-300"
                  >
                    <span className="relative z-10">
                      {contactLink.label || 'Schedule a Tour'}
                    </span>
                  </a>
                </EditableSlotGroup>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* ============================================================== */}
      {/* Tablet / Mobile Drawer                                         */}
      {/* -------------------------------------------------------------- */}
      {/* Rendered as a sibling of <header>, NOT inside it. The offer     */}
      {/* banner is fixed at z-index 60; the header is a z-50 stacking    */}
      {/* context, so a drawer nested inside the header can never rise   */}
      {/* above the banner — which trapped the close control under the   */}
      {/* banner. Keeping the drawer outside (z-70+) restores close.     */}
      {/* ============================================================== */}

      {/* Backdrop — dims the page behind the drawer and closes it on tap. */}
      <div
        onClick={closeMobileMenu}
        aria-hidden="true"
        className={`lg:hidden fixed inset-0 z-[70] bg-black/50 transition-opacity duration-300 ${
          menuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
        }`}
      />

      {/* Drawer panel */}
      <div
        className={`mobile-menu-panel lg:hidden fixed left-0 top-0 bottom-0 z-[72] w-[88%] max-w-[440px] overflow-y-auto transition-transform duration-300 ease-out ${
          menuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{
          backgroundColor: 'var(--vibe-color-primary)',
          '--bh-drawer-chrome-bg': drawerChromeBg,
          '--bh-drawer-chrome-fg': drawerChromeFg,
          '--bh-drawer-close-bg': hamburgerBg,
          '--bh-drawer-close-fg': hamburgerFg,
        } as React.CSSProperties}
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
      >
          <div className="flex min-h-full flex-col px-9 py-8">
            {/* Close button — matches the hamburger disc (Color 5 with a
                white X). */}
            <button
              onClick={closeMobileMenu}
              aria-label="Close menu"
              className="nav-drawer-close flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full transition-colors"
            >
              <CloseIcon className="h-5 w-5" />
            </button>

            {/* Brand mark — a property logo that links home. Uses the white /
                dark / primary variant chosen above (plus whitening when needed)
                so it reads on the primary drawer fill; falls back to the
                property name wordmark when the property has no logo asset. */}
            <EditableSlotGroup
              groupPath="header.drawer_logo"
              definition={runtimeDrawerLogoSlotDef}
              className="mt-10 mb-4"
            >
              <a
                href="/"
                onClick={closeMobileMenu}
                aria-label="Go to homepage"
                className="inline-flex"
              >
                {drawerLogo.image ? (
                  <VibesitesImage
                    url={drawerLogo.image}
                    alt={drawerLogo.alt || propertyName || 'Community Logo'}
                    className="h-12 w-auto max-w-[240px] object-contain"
                    sizes="240px"
                    style={whitenDrawerLogo ? { filter: 'brightness(0) invert(1)' } : undefined}
                  />
                ) : (
                  <span className="font-heading text-4xl uppercase leading-none tracking-wide text-[var(--vibe-color-text-on-primary)]">
                    {propertyName || 'Blue Haven'}
                  </span>
                )}
              </a>
            </EditableSlotGroup>

            {/* Navigation links — same set as desktop (no Schedule a Tour
                row; that CTA lives below as a pill button). */}
            <nav role="navigation" aria-label="Mobile navigation" className="flex flex-col">
              {navLinks.map((link) => (
                <React.Fragment key={`${link.href}-${link.label}`}>
                  <MobileNavLink link={link} onNavigate={closeMobileMenu} />
                  {link.children?.map((child) => (
                    <MobileNavLink
                      key={child.href}
                      link={child}
                      onNavigate={closeMobileMenu}
                      isChild
                    />
                  ))}
                </React.Fragment>
              ))}
            </nav>

            {/* Schedule a Tour CTA — same header.contact_link as the desktop
                pill, rendered as a button in the drawer instead of a nav row.
                EditableSlotGroup stays on the desktop CTA (one wrapper per
                path); this link reuses the resolved slot values. */}
            <a
              href={contactLink.url || '/contact-us'}
              target={contactLink._target || '_self'}
              onClick={closeMobileMenu}
              className="nav-drawer-cta mt-8 inline-flex items-center justify-center self-start whitespace-nowrap px-8 py-3.5 font-heading text-base font-normal uppercase tracking-[0.15em] transition-all duration-300"
            >
              {contactLink.label || 'Schedule a Tour'}
            </a>

            {/* Utility block — social, phone, address. */}
            <div className="mt-8 space-y-4">
              {/* Social icons — only platforms with a configured URL render. */}
              {socialLinks.length > 0 && (
                <div className="flex items-center gap-4">
                  {socialLinks.map(({ url, Icon, label }) => (
                    <a
                      key={label}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                      className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-[var(--vibe-color-text-on-primary)] text-[var(--vibe-color-text-on-primary)] transition-colors hover:bg-[var(--vibe-color-text-on-primary)]/10"
                    >
                      <Icon size={20} color="currentColor" aria-label={label} />
                    </a>
                  ))}
                </div>
              )}

              {/* Phone */}
              <a
                href={`tel:${phone?.replace(/[^+\d]/g, '')}`}
                className="dniphone dniphonehref block font-body text-base font-semibold text-[var(--vibe-color-text-on-primary)] transition-colors"
              >
                {/* EditableToken wraps just the inner text span so the editor
                    can target the phone number from inside the open drawer. */}
                <EditableToken
                  path="header.phone"
                  defaultField="property_call_tracking_phone"
                  defaultValue="(555) 555-5555"
                  as="span"
                  className="rise-nav-darken-hover"
                >
                  {formatPhone(phone)}
                </EditableToken>
              </a>

              {/* Address */}
              <EditableSlotGroup groupPath="header.address_link" definition={addressLinkDef}>
                <a
                  href={addressLink.url || propertyAddressUrl || '#'}
                  target={addressLink._target || '_blank'}
                  rel="noopener noreferrer"
                  className="block text-base leading-relaxed text-[var(--vibe-color-text-on-primary)]/70 transition-colors"
                >
                  <span className="rise-nav-darken-hover">
                    <span className="block">{addressLine1}</span>
                    {hasValue(addressLine2) && <span className="block">{addressLine2}</span>}
                  </span>
                </a>
              </EditableSlotGroup>
            </div>
          </div>
      </div>

      <style>{`
        /* ================================================================ */
        /* Single navigation bar                                           */
        /* ================================================================ */
        /* The header is a solid light (surface) bar on every page, sitting
           above the hero in normal flow (sticky). A subtle bottom border +
           faint shadow separate it from the content below. */
        .header-bar {
          border-bottom: 1px solid var(--vibe-color-border);
          box-shadow:
            0 1px 3px rgba(0,0,0,0.04),
            0 4px 12px rgba(0,0,0,0.03);
        }

        /* "More" / child dropdown panels sit on --vibe-color-background, whose
           contrast side may differ from the surface bar, so their links use the
           dedicated --bh-header-dropdown-fg foreground (dark theme text on a
           light background, white on a dark one). The hover affordance matches
           top-level desktop nav (.nav-tb-hover animated double-bar), so we hold
           the text color steady on hover. The !important beats the per-link
           Tailwind text color utilities (including the muted child rows). */
        .header-bar .nav-more-dropdown a,
        .header-bar .nav-more-dropdown a:hover {
          color: var(--bh-header-dropdown-fg) !important;
        }

        /* The tablet / mobile drawer sits on --vibe-color-primary (the dark
           brand color), so its links + buttons render in the on-primary
           (usually white) text color. The hover affordance for nav links is
           the animated underline from .rise-nav-darken-hover below (drawn in
           currentColor), so we hold the text color steady on hover. The close
           button is the deliberate exception — it's a contrast-aware disc
           (not an on-primary glyph), so we scope these rules to anchors and
           let the close button keep its own colors. */
        .mobile-menu-panel a {
          color: var(--vibe-color-text-on-primary);
        }
        .mobile-menu-panel a:hover {
          color: var(--vibe-color-text-on-primary);
        }

        /* Nav links: animated underline on hover (used by MobileNavLink and
           the address / phone rows in the mobile drawer). Desktop top-level
           and dropdown items use .nav-tb-hover instead. The underline is
           drawn via text-decoration so it rides the text baseline and works
           at any link padding. */
        .rise-nav-darken-hover {
          /* underline + overline so a matching top AND bottom line appear on
             hover. Both share text-decoration-color, so they fade in together
             (transparent → currentColor) only on hover. */
          text-decoration-line: underline overline;
          text-decoration-color: transparent;
          text-decoration-thickness: 1.5px;
          text-underline-offset: 6px;
          transition: text-decoration-color 0.25s ease, text-underline-offset 0.25s ease, color 0.2s ease;
        }
        .rise-nav-darken-hover:hover {
          text-decoration-color: currentColor;
          text-underline-offset: 4px;
        }
        /* Top-level desktop nav items draw their top + bottom hover lines as
           ::before / ::after pseudo-elements (rather than borders) so they can
           ANIMATE into place — matching the reference site's livelier effect.
           The lines sit at the edges of the link's vertical padding (py-1.5 ≈
           6px from the label). On hover they animate "outside in": the top
           line starts above and slides DOWN, the bottom line starts below and
           slides UP, both settling onto the label with an ease-out. */
        .nav-tb-hover {
          position: relative;
        }
        .nav-tb-hover::before,
        .nav-tb-hover::after {
          content: '';
          position: absolute;
          left: 0;
          right: 0;
          height: 2px;
          background: currentColor;
          opacity: 0;
          transition: opacity 0.2s ease, transform 0.5s cubic-bezier(0.22, 1, 0.36, 1);
          pointer-events: none;
        }
        /* Resting at the padding edges; start offsets pull each line to the
           OUTSIDE (top above, bottom below) so they converge inward on hover. */
        .nav-tb-hover::before { top: 0; transform: translateY(-7px); }
        /* Bottom line sits 2px lower than the padding edge so it clears the
           label's descenders a touch more than the top line. */
        .nav-tb-hover::after { bottom: -2px; transform: translateY(9px); }
        .nav-tb-hover:hover::before,
        .nav-tb-hover:hover::after {
          opacity: 1;
          transform: translateY(0);
        }
        /* Dropdown rows keep a full-width hit target; the .nav-tb-hover span
           only wraps the label. Light the bars from the parent <a> hover /
           focus so padding doesn't leave a dead zone around the text. */
        .header-bar .nav-more-dropdown a:hover .nav-tb-hover::before,
        .header-bar .nav-more-dropdown a:hover .nav-tb-hover::after,
        .header-bar .nav-more-dropdown a:focus-visible .nav-tb-hover::before,
        .header-bar .nav-more-dropdown a:focus-visible .nav-tb-hover::after {
          opacity: 1;
          transform: translateY(0);
        }
        /* Active page: keep BOTH lines lit persistently (same resting position
           as the hover end-state) so the current banner/interior page reads as
           selected in the nav — a top bar above and a bottom bar below the
           label. This is the same double-bar the link shows on hover, just
           held on. Applied to the matching top-level link (and the "More"
           button when the active page is in overflow) via .nav-tb-active. */
        .nav-tb-active::before,
        .nav-tb-active::after {
          opacity: 1;
          transform: translateY(0);
        }
        /* Address / phone rows in the mobile drawer wear the
           rise-nav-darken-hover class on the inner text span (not the link
           itself, so the underline doesn't run under the icon). Trigger
           the underline from the parent link's :hover/:focus-visible so
           the whole row remains the hover target. */
        .mobile-menu-panel a:hover .rise-nav-darken-hover,
        .mobile-menu-panel a:focus-visible .rise-nav-darken-hover {
          text-decoration-color: currentColor;
          text-underline-offset: 4px;
        }

        /* ================================================================ */
        /* CTA Button — filled pill                                        */
        /* ================================================================ */
        /* Solid primary fill with an on-primary label, fully rounded — matches
           the reference design's "Contact Us" button. Darkens slightly and
           lifts on hover. */
        .nav-cta-btn {
          background: var(--bh-cta, var(--vibe-color-primary));
          border: 1.5px solid var(--bh-cta, var(--vibe-color-primary));
          border-radius: 9999px;
          color: var(--bh-cta-fg, #fff);
          box-shadow: none;
        }
        .nav-cta-btn:hover {
          background: color-mix(in srgb, var(--bh-cta, var(--vibe-color-primary)), black 12%);
          border-color: color-mix(in srgb, var(--bh-cta, var(--vibe-color-primary)), black 12%);
          color: var(--bh-cta-fg, #fff);
          transform: translateY(-1px);
          box-shadow:
            0 2px 6px rgba(0,0,0,0.1),
            0 4px 16px color-mix(in srgb, var(--bh-cta, var(--vibe-color-primary)) 25%, transparent);
        }
        .nav-cta-btn:active {
          transform: translateY(0);
          box-shadow: none;
        }

        /* Drawer close disc + drawer CTA — contrast-aware against the
           primary drawer fill. Scoped under .mobile-menu-panel so these
           colors beat the panel-wide on-primary anchor rules above. */
        .mobile-menu-panel .nav-drawer-close {
          background: var(--bh-drawer-close-bg);
          color: var(--bh-drawer-close-fg);
        }
        .mobile-menu-panel .nav-drawer-close:hover {
          background: color-mix(in srgb, var(--bh-drawer-close-bg), black 8%);
        }
        .mobile-menu-panel a.nav-drawer-cta {
          background: var(--bh-cta, var(--vibe-color-primary));
          border: 1.5px solid var(--bh-cta, var(--vibe-color-primary));
          border-radius: 9999px;
          color: var(--bh-cta-fg, #fff);
          box-shadow: none;
        }
        .mobile-menu-panel a.nav-drawer-cta:hover {
          background: color-mix(in srgb, var(--bh-cta, var(--vibe-color-primary)), black 8%);
          border-color: color-mix(in srgb, var(--bh-cta, var(--vibe-color-primary)), black 8%);
          color: var(--bh-cta-fg, #fff);
          transform: translateY(-1px);
          box-shadow:
            0 2px 6px rgba(0,0,0,0.12),
            0 4px 16px rgba(0,0,0,0.08);
        }
        .mobile-menu-panel a.nav-drawer-cta:active {
          transform: translateY(0);
          box-shadow: none;
        }

      `}</style>
    </>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'Header',
  displayName: 'Header',
  tokens: {
    'header.phone': {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '(555) 555-5555',
    },
    'header.interactive_map_show_if_exists': {
      label: 'Interactive Map: Show If Exists',
      type: 'text',
      description:
        'Expose the Interactive Map page in the navigation if this token or field contains a value (e.g. custom_engrain_sightmap_url). Leave blank to hide.',
      defaultValue: '',
    },
  },
  slotGroups: {
    'header.logo': {
      label: 'Logo',
      fields: {
        image: { label: 'Logo Image', type: 'image', defaultField: 'property_primary_logo', suggestedFields: ['property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
      },
    },
    'header.drawer_logo': {
      label: 'Drawer Logo',
      fields: {
        image: {
          label: 'Logo Image',
          type: 'image',
          defaultField: 'property_primary_logo',
          suggestedFields: [
            'property_primary_logo',
            'property_mono_white_logo',
            'property_mono_black_logo',
          ],
        },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
      },
    },
    'header.contact_link': {
      label: 'Header CTA',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/contact-us' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.address_link': {
      label: 'Address Link',
      fields: {
        address: {
          label: 'Address',
          type: 'text',
          defaultField: 'property_full_address',
          defaultValue: '123 Main Street, Atlanta, GA 30313',
        },
        // URL has no defaultField — defaults to usePropertyAddressUrl() at
        // render time. Editor overrides still win.
        url: {
          label: 'URL',
          type: 'url',
        },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
  },
  collectionBindings: {
    'header.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(Header as any).contentSchema = contentSchema;

export default Header;
